import { useMemo } from 'react'
import type { SetupStatus } from '../types'
import type { SetupEntryIntent } from './onboardingEntryRouting'

type Params = {
  isAuthenticated: boolean
  setupStatus: SetupStatus | null
  setupLoading: boolean
  sessionBootstrapLoading: boolean
  forceSetupWizardVisible?: boolean
  setupEntryIntent?: SetupEntryIntent
}

export type AppViewMode = 'checking-setup' | 'onboarding-entry' | 'setup-wizard' | 'admin-shell' | 'auth-screen'

export const resolveAppViewMode = ({
  isAuthenticated,
  setupStatus,
  setupLoading,
  sessionBootstrapLoading,
  forceSetupWizardVisible = false,
  setupEntryIntent = 'welcome',
}: Params) => {
  const setupResolved = setupStatus !== null
  const checkingSetup =
    !isAuthenticated && ((!setupResolved && setupLoading) || sessionBootstrapLoading)
  const shouldShowSetup = setupStatus?.firstInstallCompleted === false || forceSetupWizardVisible

  const mode: AppViewMode = checkingSetup
    ? 'checking-setup'
    : shouldShowSetup
      ? isAuthenticated || forceSetupWizardVisible || setupEntryIntent === 'setup'
        ? 'setup-wizard'
        : 'onboarding-entry'
      : isAuthenticated
        ? 'admin-shell'
        : 'auth-screen'

  return {
    mode,
    checkingSetup,
    shouldShowSetup,
  }
}

export const useAppViewState = ({
  isAuthenticated,
  setupStatus,
  setupLoading,
  sessionBootstrapLoading,
  forceSetupWizardVisible = false,
  setupEntryIntent = 'welcome',
}: Params) =>
  useMemo(() => {
    return resolveAppViewMode({
      isAuthenticated,
      setupStatus,
      setupLoading,
      sessionBootstrapLoading,
      forceSetupWizardVisible,
      setupEntryIntent,
    })
  }, [
    forceSetupWizardVisible,
    isAuthenticated,
    sessionBootstrapLoading,
    setupLoading,
    setupStatus,
    setupEntryIntent,
  ])
